/**
 * CorporateAgreementsSection — list + add/edit/remove + save for a
 * patient's corporate (employer / sponsor) agreements.
 *
 * Drops into Contact Details (per ticket scope) once PROD-728 merges.
 * Mirrors `HealthInsuranceSection` but with corporate-specific
 * variations:
 *   - No `isPrimary` invariant — Flutter doesn't model it on corporates.
 *   - Data source is the patient body (`patient.corporateAgreementResponse`),
 *     not a sibling GET endpoint. Section accepts the seed list via
 *     prop so the parent (tab) reads from its own `usePatient` query.
 *   - Save calls `useSaveCorporateAgreements` → POST /Patients/PatientCorporate.
 *
 * Form fields (Flutter parity): card photos (front/back), sponsor
 * (typeahead), member ID, eligibility/expiration dates, discount type
 * + per-billing-group breakdown, and a verbatim confirmation checkbox.
 * Card photos + discount breakdown were the F-PAT-243 deep-edit gap,
 * now built via the shared payer components.
 */

import { useEffect, useState } from 'react';
import { RecordListAdd, RecordListEmpty } from '@/shared/ui/components/record-row-card';
import { Button } from '@scripsteam/scrips-signal-ds';
import { toast } from 'sonner';
import { useSaveCorporateAgreements } from '@/patients/services/use-save-corporate-agreements';
import { useBillingSettings } from '@/billing/services/use-billing-settings';
import { useAuthStore } from '@/auth/auth-store';
import {
  CorporateAgreementRowComponent,
  type CorporateRow,
} from './corporate-agreement-row';
import type { CorporateList } from '@/types/patient-api';

export interface CorporateAgreementsSectionProps {
  patientId: string | null | undefined;
  organizationId: string;
  /** Seed list — comes from `patient.corporateAgreementResponse`. */
  initialList: CorporateList[];
  readOnly?: boolean;
}

export function CorporateAgreementsSection({
  patientId,
  organizationId,
  initialList,
  readOnly,
}: CorporateAgreementsSectionProps) {
  // PROD-2743 · switch ON: the DS `add` glyph (one icon system, 16px inline); OFF = today's lucide.
  const saveMutation = useSaveCorporateAgreements();
  const billingSettings = useBillingSettings(organizationId);
  const billingGroups = billingSettings.data?.billingGroups ?? [];
  const currencyCode =
    useAuthStore((s) => s.user?.organizationCurrency) ?? 'AED';
  const [rows, setRows] = useState<CorporateRow[]>(() =>
    initialList.map((r) => ({ ...r, isEdit: false })),
  );
  const [isDirty, setIsDirty] = useState(false);

  // Re-seed whenever the parent passes a new snapshot (post-save refetch
  // updates `patient.corporateAgreementResponse`, which we propagate
  // back through props).
  useEffect(() => {
    setRows(initialList.map((r) => ({ ...r, isEdit: false })));
    setIsDirty(false);
  }, [initialList]);

  const handleAdd = () => {
    if (readOnly) return;
    setRows((prev) => [
      ...prev,
      {
        patientId: patientId ?? undefined,
        policyNumber: '',
        isVerified: false,
        isReviewed: true,
        isDeleted: false,
        isEdit: true,
      },
    ]);
    setIsDirty(true);
  };

  const handleRowChange = (i: number, next: CorporateRow) => {
    setRows((prev) => prev.map((r, idx) => (idx === i ? next : r)));
    setIsDirty(true);
  };

  const handleCancel = (i: number) => {
    setRows((prev) => {
      const row = prev[i];
      if (!row) return prev;
      if (!row.corporateId) {
        return prev.filter((_, idx) => idx !== i);
      }
      const snapshot = initialList.find((s) => s.corporateId === row.corporateId);
      if (snapshot) {
        return prev.map((r, idx) => (idx === i ? { ...snapshot, isEdit: false } : r));
      }
      return prev.map((r, idx) => (idx === i ? { ...r, isEdit: false } : r));
    });
  };

  const handleRemove = (i: number) => {
    if (readOnly) return;
    setRows((prev) => {
      const row = prev[i];
      if (!row) return prev;
      if (!row.corporateId) {
        return prev.filter((_, idx) => idx !== i);
      }
      return prev.map((r, idx) =>
        idx === i ? { ...r, isDeleted: true, isEdit: false } : r,
      );
    });
    setIsDirty(true);
  };

  const handleSave = async () => {
    if (!patientId) {
      toast.error('Cannot save — patient not loaded yet.');
      return;
    }
    if (rows.some((r) => r.isEdit && !r.isDeleted)) {
      toast.error('Finish editing rows before saving.');
      return;
    }
    // Duplicate policy-number guard — Flutter parity (insurance has
    // the same rule; corporates do too).
    const liveRows = rows.filter((r) => !r.isDeleted);
    const seen = new Set<string>();
    for (const r of liveRows) {
      const key = (r.policyNumber ?? '').trim().toLowerCase();
      if (!key) continue;
      if (seen.has(key)) {
        toast.error(`Employee number "${r.policyNumber}" already used on another row.`);
        return;
      }
      seen.add(key);
    }
    try {
      const payload: CorporateList[] = rows.map(({ isEdit: _isEdit, ...rest }) => rest);
      await saveMutation.mutateAsync({ patientId, corporateList: payload });
      toast.success('Corporate agreements saved');
      setIsDirty(false);
    } catch {
      // Global error handler.
    }
  };

  const visibleRows = rows
    .map((row, i) => ({ row, i }))
    .filter(({ row }) => !row.isDeleted);

  if (!patientId) {
    return (
      <RecordListEmpty>Save the patient first before adding corporate agreements.</RecordListEmpty>
    );
  }

  return (
    // The approved design's PaRecordList: the rows 12 apart, the empty line, the "Add …" row at the foot.
    <div data-slot="record-list">
      {visibleRows.length === 0 ? (
        <>
          <RecordListEmpty>No corporate agreement on file.</RecordListEmpty>
          {!readOnly ? <RecordListAdd onClick={handleAdd}>Add corporate agreement</RecordListAdd> : null}
        </>
      ) : (
        <>
          {visibleRows.map(({ row, i }) => (
            <CorporateAgreementRowComponent
              key={row.corporateId || `new-${i}`}
              row={row}
              isOnly={visibleRows.length <= 1}
              organizationId={organizationId}
              billingGroups={billingGroups}
              currencyCode={currencyCode}
              readOnly={readOnly}
              onChange={(next) => handleRowChange(i, next)}
              onCancel={() => handleCancel(i)}
              onRemove={() => handleRemove(i)}
            />
          ))}
          {!readOnly ? (
            <RecordListAdd onClick={handleAdd}>Add corporate agreement</RecordListAdd>
          ) : null}
        </>
      )}

      {!readOnly ? (
        <div className="flex justify-end pt-2">
          <Button
            type="button"
            size="sm"
            onClick={() => void handleSave()}
            disabled={
              !isDirty ||
              saveMutation.isPending ||
              rows.some((r) => r.isEdit && !r.isDeleted)
            }
          >
            {saveMutation.isPending ? 'Saving…' : 'Save corporate agreements'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
